// src/components/cycles/CycleDetails.jsx
import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Col,
  Empty,
  Flex,
  Form,
  Input,
  Row,
  Space,
  Typography,
  message,
  Popconfirm,
  Tag,
  Tooltip,
} from "antd";
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  SaveOutlined,
  CalendarOutlined,
  DragOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { v4 as uuid } from "uuid";
import SeanceCatalog from "./SeanceCatalog";
import FrequencyDrawer from "./FrequencyDrawer";
import { DRAG_TYPE_SEANCE } from "../Utils/Constants";
import Constants from "../Utils/Constants";

const { Title, Text, Paragraph } = Typography;

const api = axios.create({
  baseURL: Constants.SERVER_URL,
});

api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const API = {
  get: (key) => `/cycle/${encodeURIComponent(key)}`,
  update: (key) => `/update-cycle/${encodeURIComponent(key)}`,
  add: (key) => `/${encodeURIComponent(key)}/add-session`,
  remove: (key) => `/${encodeURIComponent(key)}/remove-session`,
};

const WEEKDAY_TO_CODE = {
  DAY_ONE: "mon",
  DAY_TWO: "tue",
  DAY_THREE: "wed",
  DAY_FOUR: "thu",
  DAY_FIVE: "fri",
  DAY_SIX: "sat",
  DAY_SEVEN: "sun",
};

const DAY_ORDER = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

const DAY_LABELS = {
  mon: "Lun",
  tue: "Mar",
  wed: "Mer",
  thu: "Jeu",
  fri: "Ven",
  sat: "Sam",
  sun: "Dim",
};

export default function CycleDetails() {
  const { key } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [items, setItems] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const pendingSeanceRef = useRef(null);

  async function load() {
    setLoading(true);

    try {
      const { data } = await api.get(API.get(key));

      form.setFieldsValue({
        name: data?.name || "",
        description: data?.description || "",
      });

      const map = new Map();

      (data?.SessionDays || data?.sessions || []).forEach((row) => {
        const sessionId = row?.SessionId ?? row?.sessionId ?? row?.id;
        const session = row?.Session || {};

        const title = session?.name ?? row?.title ?? row?.name ?? "Séance";
        const description = session?.description ?? row?.description ?? "";
        const videoUrl = session?.videoUrl ?? row?.videoUrl ?? "";
        const dayCode = WEEKDAY_TO_CODE[row?.weekDay] ?? null;

        if (!map.has(sessionId)) {
          map.set(sessionId, {
            id: uuid(),
            baseSessionId: sessionId,
            title,
            description,
            videoUrl,
            days: new Set(),
          });
        }

        if (dayCode) {
          map.get(sessionId).days.add(dayCode);
        }
      });

      const customSessions = Array.from(map.values()).map((session) => ({
        ...session,
        frequency: {
          kind: "custom",
          days: Array.from(session.days),
        },
      }));

      const flexibleSessions = (data?.WeeklyCycleFlexibleSessions || []).map(
        (item) => ({
          id: uuid(),
          baseSessionId: item?.SessionId,
          title: item?.Session?.name ?? item?.Session?.title ?? "Séance libre",
          description: item?.Session?.description || "",
          videoUrl: item?.Session?.videoUrl || "",
          frequency: {
            kind: "free",
            amount: item.amount,
            unit: item.unit,
          },
        })
      );

      setItems([...customSessions, ...flexibleSessions]);
    } catch (error) {
      console.error(error);
      message.error("Impossible de charger le cycle.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  async function saveMeta(values) {
    setSaving(true);

    try {
      await api.put(API.update(key), {
        name: values.name,
        description: values.description,
      });

      message.success("Cycle enregistré.");
navigate("/cycles2", { replace: true });
    } catch (error) {
      console.error(error);
      message.error("Échec d’enregistrement.");
    } finally {
      setSaving(false);
    }
  }

  function handleDragOver(event) {
    if (
      event.dataTransfer?.types?.includes(DRAG_TYPE_SEANCE) ||
      event.dataTransfer?.types?.includes("text/plain")
    ) {
      event.preventDefault();
    }
  }

  function handleDrop(event) {
    event.preventDefault();

    let raw = event.dataTransfer.getData(DRAG_TYPE_SEANCE);

    if (!raw) {
      raw = event.dataTransfer.getData("text/plain");
    }

    if (!raw) {
      message.error("Échec du transfert de la séance.");
      return;
    }

    try {
      const payload = JSON.parse(raw);

      pendingSeanceRef.current = {
        id: payload.id,
        key: payload.key,
        name: payload.name,
        description: payload.description || "",
        videoUrl: payload.videoUrl || "",
      };

      setDrawerOpen(true);
    } catch {
      message.error("Données de séance invalides.");
    }
  }

  async function confirmFrequency(frequency) {
    const base = pendingSeanceRef.current;

    if (!base) return;

    try {
      await api.post(API.add(key), {
        sessionName: base.name,
        frequency,
      });

      setItems((prev) => [
        ...prev,
        {
          id: uuid(),
          title: base.name,
          description: base.description,
          videoUrl: base.videoUrl,
          baseSessionId: base.id,
          frequency,
        },
      ]);

      message.success("Séance ajoutée.");
    } catch (error) {
      console.error(error);
      message.error("Impossible d’ajouter la séance.");
    } finally {
      pendingSeanceRef.current = null;
      setDrawerOpen(false);
    }
  }

  async function removeItem(item) {
    try {
      await api.request({
        method: "DELETE",
        url: API.remove(key),
        data: {
          sessionName: item.title,
        },
      });

      setItems((prev) => prev.filter((current) => current.id !== item.id));
      message.success("Séance retirée.");
    } catch (error) {
      console.error(error);
      message.error("Suppression impossible.");
    }
  }

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Flex align="center" justify="space-between">
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/cycles")}
          >
            Retour
          </Button>

          <Title level={3} style={{ margin: 0 }}>
            Détails du cycle
          </Title>
        </Space>
      </Flex>

      <Card loading={loading}>
        <Form layout="vertical" form={form} onFinish={saveMeta}>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                label="Nom du cycle"
                name="name"
                rules={[{ required: true, message: "Nom requis" }]}
              >
                <Input placeholder="Ex. Programme printemps" />
              </Form.Item>
            </Col>

            <Col xs={24} md={16}>
              <Form.Item label="Description" name="description">
                <Input.TextArea
                  rows={1}
                  placeholder="Résumé du cycle"
                  autoSize={{ minRows: 1, maxRows: 4 }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Flex gap={8} justify="flex-end">
            <Button
              htmlType="submit"
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
            >
              Enregistrer
            </Button>
          </Flex>
        </Form>
      </Card>

      <Row gutter={16}>
        <Col xs={24} md={10}>
          <SeanceCatalog dragType={DRAG_TYPE_SEANCE} />
        </Col>

        <Col xs={24} md={14}>
          <Card
            title={
              <Space>
                <CalendarOutlined />
                Séances du cycle
                <Tag>{items.length}</Tag>
              </Space>
            }
            bodyStyle={{ padding: 0 }}
          >
            <div
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              style={{
                minHeight: 300,
                padding: 16,
                borderRadius: 12,
                background:
                  items.length === 0
                    ? "rgba(22,119,255,0.03)"
                    : "transparent",
              }}
            >
              {items.length === 0 ? (
                <Empty description="Glisse une séance ici pour l’ajouter au cycle." />
              ) : (
                <Space direction="vertical" style={{ width: "100%" }}>
                  {items.map((item) => (
                    <Card
                      key={item.id}
                      size="small"
                      hoverable
                      style={{
                        borderRadius: 12,
                        borderLeft: "4px solid #1677ff",
                      }}
                      extra={
                        <Popconfirm
                          title="Retirer cette séance ?"
                          okText="Retirer"
                          cancelText="Annuler"
                          okButtonProps={{ danger: true }}
                          onConfirm={() => removeItem(item)}
                        >
                          <Tooltip title="Retirer">
                            <Button
                              size="small"
                              type="text"
                              danger
                              icon={<DeleteOutlined />}
                            />
                          </Tooltip>
                        </Popconfirm>
                      }
                    >
                      <Space
                        direction="vertical"
                        size={8}
                        style={{ width: "100%" }}
                      >
                        <Flex align="center" justify="space-between" gap={12}>
                          <Space>
                            <DragOutlined style={{ color: "#1677ff" }} />
                            <Text strong>{item.title}</Text>
                          </Space>

                          <FrequencyBadge frequency={item.frequency} />
                        </Flex>

                        {item.description && (
                          <Paragraph
                            type="secondary"
                            ellipsis={{ rows: 2 }}
                            style={{ marginBottom: 0 }}
                          >
                            {item.description}
                          </Paragraph>
                        )}
                      </Space>
                    </Card>
                  ))}
                </Space>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      <FrequencyDrawer
        open={drawerOpen}
        onCancel={() => {
          pendingSeanceRef.current = null;
          setDrawerOpen(false);
        }}
        onConfirm={confirmFrequency}
      />
    </Space>
  );
}

function FrequencyBadge({ frequency }) {
  if (!frequency) return null;

  if (frequency.kind === "free") {
    return (
      <Tag color="purple">
        {frequency.amount}{" "}
        {frequency.unit === "per_day" ? "/ jour" : "/ semaine"}
      </Tag>
    );
  }

  if (frequency.kind === "custom") {
    const days = (frequency.days || [])
      .slice()
      .sort((a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b))
      .map((day) => DAY_LABELS[day] || day);

    return <Tag color="blue">Jours : {days.join(", ") || "—"}</Tag>;
  }

  return null;
}